import { Box, Paper, Table, TableBody, TableContainer } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import { useGetActivityLog } from '../../../reactQuery/Activities/activities';
import { formatLocaleDate } from '../../../helpers/dateHelpers';
import {
  ValueAgentRow,
  ValueBooleanRow,
  ValueContactListRow,
  ValueDateRow,
  ValueRow,
  ValueTextRow,
} from '../../../components/table';
import { UpsertActivityLogDialog } from '../dialogs/UpsertActivityLogDialog';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { EditAction } from '../../../components/FloatingActionsButtons/components/EditAction';
import { DeleteActivityLogDialog } from '../dialogs/DeleteActivityLogDialog';
import { DeleteAction } from '../../../components/FloatingActionsButtons/components/DeleteAction';
import { ROUTES } from '../../../routes/Routes';
import { OngoingActivityAlert } from './components/OngoingActivityAlert';
import { IncludedInContactIntervalStatus } from '../Overview/components/IncludedInContactIntervalStatus';

export const ActivityLogDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const navigate = useNavigate();
  const params = useParams();

  const activityLogId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: ROUTES.activityLog.overview,
    },
  ];

  const {
    data: activityLog,
    isLoading,
    error,
  } = useGetActivityLog(activityLogId);

  const handleActityLogNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'contact',
      }),
      {
        variant: 'error',
      }
    );
    navigate(ROUTES.activityLog.overview);
  };

  useEffect(() => {
    if (Number.isNaN(activityLogId)) {
      handleActityLogNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleActityLogNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [activityLogId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!activityLog) {
    navigate(ROUTES.activityLog.overview);
    return <Box>Error</Box>;
  }

  return (
    <Fragment>
      <PageTitle title={t('activities:title')} space={'double'}>
        <Breadcrumbs
          crumbs={crumbs}
          current={formatLocaleDate(activityLog.performedAt)}
        />
      </PageTitle>

      {activityLog.ongoingActivity && (
        <OngoingActivityAlert ongoingActivity={activityLog.ongoingActivity} />
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <ValueDateRow
              label={t('entities:common.date')}
              date={activityLog.performedAt}
            />
            <ValueRow
              label={t('entities:activityLog.action')}
              value={activityLog.action.name}
            />
            <ValueContactListRow
              label={t('entities:activityLog.contacts')}
              contacts={activityLog.contacts}
            />
            <ValueRow
              label={t('entities:activityLog.client')}
              value={activityLog.client.name}
            />
            <ValueRow
              label={t('entities:activityLog.group')}
              value={activityLog.client.group?.name}
            />
            <ValueAgentRow
              label={t('entities:activityLog.agent')}
              agent={activityLog.agent}
            />
            <ValueTextRow
              label={t('entities:activityLog.summary')}
              value={activityLog.summary}
            />
            <ValueBooleanRow
              label={t('entities:activityLog.excludeFromContactInterval')}
              value={activityLog.excludeFromContactInterval}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <FloatingActionsButtons>
        <DeleteAction
          onClick={() => {
            setShowDeleteDialog(true);
          }}
        />
        <EditAction
          onClick={() => {
            setShowEditDialog(true);
          }}
        />
      </FloatingActionsButtons>

      <UpsertActivityLogDialog
        activityLog={activityLog}
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />
      <DeleteActivityLogDialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        activityLogId={activityLog.id}
      />
    </Fragment>
  );
};
